package dev.youndie.site.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initViddikPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("viddik.md", FrontMatterElement.Builder { addScalar("title", "viddik"); addScalar("searchTitle", "Compose Multiplatform screenshot testing without an emulator"); addScalar("description", "Screenshot tests that render through a real Compose Desktop window instead of LayoutLib, so goldens hold across macOS, Linux and Windows."); addScalar("library", "viddik"); addScalar("post", "/blog/screenshots-without-an-emulator"); addScalar("docs", "https://youndie.github.io/viddik/"); }))
}

@Page("/viddik")
@Layout(".components.layouts.LibraryLayout")
@Composable
fun ViddikPage() {
    org.jetbrains.compose.web.dom.H2(attrs = { id("why-it-exists") }) {
        org.jetbrains.compose.web.dom.Text("Why it exists")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Android has Paparazzi, which renders through LayoutLib. Compose Multiplatform does not, because LayoutLib is Android's renderer and the code under test is not Android code. viddik renders through a real Compose Desktop window on Skiko, on a plain JVM — the same renderer that will actually draw the component.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("one-annotation-two-outputs") }) {
        org.jetbrains.compose.web.dom.Text("One annotation, two outputs")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""@ViddikScreenshot
@Composable
fun EmptyBasket() = BasketScreen(state = Basket.empty)
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A KSP processor collects every annotated composable into a registry, and that registry is used twice: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ViddikEngine")
        }
        org.jetbrains.compose.web.dom.Text(" captures each one to PNG and diffs it against the golden as a JUnit 5 test, and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ViddikShowroom")
        }
        org.jetbrains.compose.web.dom.Text(" renders the ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("same")
        }
        org.jetbrains.compose.web.dom.Text(" registry as a live component gallery. One set of declarations, so the browser cannot drift from the tests.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("fixtures-can-be-declared-with-preview") }) {
        org.jetbrains.compose.web.dom.Text("Fixtures can be declared with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@Preview")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Since 0.3.0 ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@ViddikScreenshot")
        }
        org.jetbrains.compose.web.dom.Text(" also works as a bare marker, with the details read off ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@Preview")
        }
        org.jetbrains.compose.web.dom.Text(" — the same annotation the IDE preview pane and Android's own screenshot tooling read, since Compose Multiplatform 1.12 ships it in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("commonMain")
        }
        org.jetbrains.compose.web.dom.Text(" under Android's fully-qualified name.")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""@ViddikScreenshot
@PreviewWrapper(AppPreviewTheme::class)
@PreviewLightDark
@Composable
fun PrimaryButton() { ... }
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("name")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("group")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("widthDp")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("heightDp")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("uiMode")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("fontScale")
        }
        org.jetbrains.compose.web.dom.Text(", the size in a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("spec:")
        }
        org.jetbrains.compose.web.dom.Text(" device, repeated and multipreview annotations, and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@PreviewWrapper")
        }
        org.jetbrains.compose.web.dom.Text(" are all read. Bare ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@Preview")
        }
        org.jetbrains.compose.web.dom.Text(" is not scanned: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@ViddikScreenshot")
        }
        org.jetbrains.compose.web.dom.Text(" stays the opt-in, because most previews in an application exist for the IDE and cannot render headless.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("install") }) {
        org.jetbrains.compose.web.dom.Text("Install")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""// build.gradle.kts of the module holding the fixtures
plugins {
    id("com.google.devtools.ksp") version "<KSP_VERSION>"
    id("io.github.youndie.viddik") version "<VERSION>"
}
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The plugin adds the artefacts, puts the processor on the right ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ksp*")
        }
        org.jetbrains.compose.web.dom.Text(" configuration for your module's shape — ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("kspDesktopTest")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("kspJvmTest")
        }
        org.jetbrains.compose.web.dom.Text(" or ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("kspTest")
        }
        org.jetbrains.compose.web.dom.Text(", and the per-target coordinates where a non-KMP module needs them — and registers ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("viddikRecord")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("viddikVerify")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("viddikDesignParity")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("viddikShowroom")
        }
        org.jetbrains.compose.web.dom.Text(". Wiring that by hand is still possible with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("viddik { addDependencies = false }")
        }
        org.jetbrains.compose.web.dom.Text(", but getting the configuration name wrong produces ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("no error")
        }
        org.jetbrains.compose.web.dom.Text(", only zero generated tests, which is the reason the plugin exists.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("From 0.4.0 viddik is on Maven Central as ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("io.github.youndie.viddik")
        }
        org.jetbrains.compose.web.dom.Text(", so no repository has to be declared for it. Up to 0.3.3 it was ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ru.workinprogress")
        }
        org.jetbrains.compose.web.dom.Text(" on the snapshot server, and those versions stay there.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("0.2.x through 0.5.x require Compose Multiplatform 1.12.x; 0.1.x is the 1.11.x line.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-browser-runs-on-the-phone-too") }) {
        org.jetbrains.compose.web.dom.Text("The browser runs on the phone too")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Since 0.5.0 the same registry opens in an Android activity and an iOS view controller, not only in the desktop window — a component library is judged on a phone, and a browser that opens on the machine that ran the build and nowhere else is half a browser.")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""viddik { showroomTargets = true }
""") } }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""class ShowroomActivity : ViddikShowroomActivity() {
    override val components = GeneratedViddikRegistry.components
}
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The flag is what moves the generated registry out of the test source set — which is never compiled into an application — and into ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("commonMain")
        }
        org.jetbrains.compose.web.dom.Text(", so it exists for every target the module has. The hosts come from ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("viddik-showroom")
        }
        org.jetbrains.compose.web.dom.Text(", the goldens keep working unchanged, and the list has a search field over it. The registry is passed in rather than looked up, so a fixture that stopped compiling is a build error instead of an empty gallery at launch.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("and-a-fixture-can-be-measured-against-its-design") }) {
        org.jetbrains.compose.web.dom.Text("And a fixture can be measured against its design")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Goldens answer \"did the rendering change\". ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("viddikDesignParity")
        }
        org.jetbrains.compose.web.dom.Text(", also 0.5.0, answers the question that comes first while a screen is being built: how far it is from the design it was built to. Put the exported PNG beside the golden under ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("design/")
        }
        org.jetbrains.compose.web.dom.Text(", and the task reports a percentage per fixture — reports rather than fails, because a component library rarely has a reference for every state, and a number to work from is worth more than a red build. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("designStrict")
        }
        org.jetbrains.compose.web.dom.Text(" turns it into a gate for the module where matching the design is the acceptance criterion.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("why-goldens-travel") }) {
        org.jetbrains.compose.web.dom.Text("Why goldens travel")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Because capture goes through Compose Desktop rather than a platform toolkit, the same golden holds on macOS, Linux and Windows — which is what makes goldens reviewable in a pull request instead of noise every time CI changes machines.")
    }
}
